import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { motion } from "framer-motion";
import { trackEvent } from "../utils/analytics";

function ArrowRight() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 12h14"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13 5l7 7-7 7"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronDown() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function QA({ q, a }) {
  return (
    <details className="group rounded-2xl border border-blue-100 bg-white shadow-sm">
      <summary className="cursor-pointer list-none px-6 py-5 flex items-center justify-between gap-4">
        <div className="text-base md:text-lg font-extrabold text-blue-900">{q}</div>
        <span className="text-blue-900/70 group-open:rotate-180 transition">
          <ChevronDown />
        </span>
      </summary>
      <div className="px-6 pb-6 text-gray-700 leading-relaxed">{a}</div>
    </details>
  );
}

export default function WelcomeScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const PUBLIC = process.env.PUBLIC_URL || "";

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ---------------------------
  // Auth + membership (PUBLIC page)
  // ---------------------------
  const isSignedIn = !!user;
  const primaryLabel = useMemo(() => {
    if (isSignedIn) return "Go to Dashboard";
    return "Start Free Trial";
  }, [isSignedIn]);

  const headerLabel = useMemo(() => {
    if (isSignedIn) return "Go to Dashboard";
    return "Get Started";
  }, [isSignedIn]);

  const headerCtaClass = useMemo(() => {
    const base =
      "inline-flex items-center gap-2 px-4 sm:px-5 py-2 rounded-xl transition text-xs sm:text-sm";
    if (isScrolled) {
      return isSignedIn
        ? `${base} bg-blue-950 text-white font-extrabold shadow hover:shadow-md`
        : `${base} bg-blue-950 text-white font-semibold shadow hover:shadow-md`;
    }
    return isSignedIn
      ? `${base} bg-white text-blue-950 font-extrabold shadow hover:shadow-md`
      : `${base} bg-white/90 text-blue-950 font-semibold border border-white/40 hover:bg-white`;
  }, [isScrolled, isSignedIn]);

  // ---------------------------
  // Contact mailtos
  // ---------------------------
  const CONTACT_BODY = [
    "Name:",
    "Company:",
    "City:",
    "Phone:",
    "What would you like to know about Scucci Systems and our Estate Sales Product?",
  ].join("\n");

  const CONTACT_MAILTO = `mailto:support@scuccisystems.com?subject=${encodeURIComponent(
    "Scucci Systems Inquiry"
  )}&body=${encodeURIComponent(CONTACT_BODY)}`;

  const trackNav = (to, placement) => {
    const suffix = placement ? `__${placement}` : "";
    trackEvent(`nav_home_to_${to}${suffix}`);
  };

  const goTo = (destination, placement) => {
    const target = destination.replace("/", "") || "home";
    trackNav(target, placement);
    navigate(destination);
  };

  const goPrimary = (placement) => {
    if (isSignedIn) return navigate("/estate-sales-pipeline");
    trackNav("register", placement);
    return navigate("/register");
  };

  // ---------------------------
  // Images (optional; safe fallbacks)
  // ---------------------------
  const HERO_IMAGE = `${PUBLIC}/images/hero/welcome-hero.jpg`;
  const HERO_FALLBACK = `${PUBLIC}/images/hero/momestate132.jpg`;
  const [heroSrc, setHeroSrc] = useState(HERO_IMAGE);

  const DASHBOARD_IMAGE = `${PUBLIC}/images/hero/herosalesnapshot.png?v=2`;
  const DASHBOARD_FALLBACK = `${PUBLIC}/images/product/InventoryScreen.png`;

  const TRUST_IMAGE = `${PUBLIC}/images/hero/momestate495.jpg`;

  const START_SMALL_IMAGE = `${PUBLIC}/images/hero/startsmall.jpg`;
  const START_SMALL_FALLBACK = TRUST_IMAGE;

  const PHOTO_EFFICIENCY_IMAGE = `${PUBLIC}/images/hero/inventory-dashboard-hero.png`;
  const PHOTO_EFFICIENCY_FALLBACK = `${PUBLIC}/images/hero/herosalesnapshot.png?v=2`;
  const PHOTO_SOURCE_IMAGE = `${PUBLIC}/images/hero/example-dashboard-hero.jpg`;
  const PHOTO_SOURCE_FALLBACK = HERO_IMAGE;
  const STATS_SECTION_IMAGE = `${PUBLIC}/images/hero/stats-section-breakdown.png`;
  const STATS_SECTION_FALLBACK = DASHBOARD_IMAGE;
  const STATS_UNSOLD_IMAGE = `${PUBLIC}/images/hero/stats-top-unsold-by-category.png`;
  const STATS_UNSOLD_FALLBACK = DASHBOARD_IMAGE;

  const OPS_IMAGE = TRUST_IMAGE;
  const OPS_FALLBACK = HERO_FALLBACK;

  return (
    <div className="w-full flex flex-col bg-white">
      {/* Sticky header */}
      <header className="fixed top-0 inset-x-0 z-50">
        <div
          className={
            isScrolled
              ? "absolute inset-0 bg-white/90 backdrop-blur border-b border-gray-200"
              : "absolute inset-0 bg-blue-950/55 backdrop-blur"
          }
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span
              className={
                isScrolled
                  ? "text-blue-950 text-lg font-extrabold tracking-wider"
                  : "text-white text-lg font-extrabold tracking-wider"
              }
              aria-label="Scucci Systems"
            >
              Scucci Systems
            </span>
            <span
              className={
                isScrolled
                  ? "hidden md:inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-950 border border-blue-100"
                  : "hidden md:inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white/90"
              }
            >
              Estate Sales
            </span>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => goTo("/about", "header")}
              className={
                isScrolled
                  ? "px-3 sm:px-4 py-2 border border-gray-200 text-blue-950 text-xs sm:text-sm font-semibold rounded-xl hover:bg-gray-50 transition"
                  : "px-3 sm:px-4 py-2 border border-white/20 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-white/10 transition"
              }
            >
              About
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => goTo("/pricing", "header")}
              className={
                isScrolled
                  ? "px-3 sm:px-4 py-2 border border-gray-200 text-blue-950 text-xs sm:text-sm font-semibold rounded-xl hover:bg-gray-50 transition"
                  : "px-3 sm:px-4 py-2 border border-white/20 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-white/10 transition"
              }
            >
              Pricing
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => goPrimary("header")}
              className={headerCtaClass}
            >
              {headerLabel}
              {isSignedIn ? <ArrowRight /> : null}
            </motion.button>

            {!isSignedIn && (
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate("/signin")}
                className={
                  isScrolled
                    ? "px-4 sm:px-5 py-2 border border-gray-200 text-blue-950 text-xs sm:text-sm font-bold rounded-xl hover:bg-gray-50 transition"
                    : "px-4 sm:px-5 py-2 border border-white/25 text-white text-xs sm:text-sm font-bold rounded-xl hover:bg-white/10 transition"
                }
              >
                Sign In
              </motion.button>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative w-full overflow-hidden border-b border-blue-900/40 min-h-[680px] lg:min-h-[720px]">
        <img
          src={heroSrc}
          onError={() => {
            if (heroSrc !== HERO_FALLBACK) setHeroSrc(HERO_FALLBACK);
          }}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-center"
          draggable="false"
        />
        <div className="absolute inset-0 bg-blue-950/34" />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-950/95 via-blue-950/82 to-blue-950/42" />
        <div className="absolute inset-y-0 left-0 w-[54%] bg-[linear-gradient(90deg,rgba(7,19,67,0.82)_0%,rgba(7,19,67,0.58)_52%,rgba(7,19,67,0)_100%)]" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-blue-950/26 to-transparent" />

        <div className="relative z-10 mx-auto max-w-[92rem] px-4 sm:px-6 pt-28 pb-10 lg:pb-12">
          <div className="grid lg:grid-cols-[minmax(0,0.84fr)_minmax(840px,1.36fr)] gap-8 lg:gap-10 items-center">
            <div className="text-white max-w-[600px] -mt-3 lg:-mt-10">
              <div className="inline-flex flex-wrap items-center gap-3 mb-7">
                <span className="inline-flex items-center rounded-full bg-white/12 px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.12em] text-white/92 backdrop-blur border border-white/16">
                  Built for estate sales
                </span>
                <span className="inline-flex items-center rounded-full bg-white/12 px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.12em] text-white/92 backdrop-blur border border-white/16">
                  Not a POS system
                </span>
              </div>

              <motion.h1
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-[40px] sm:text-[54px] lg:text-[66px] font-extrabold leading-[0.94] tracking-[-0.035em] max-w-[620px]"
              >
                Run a cleaner, more organized sale.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="mt-7 text-[18px] sm:text-[20px] text-white/84 max-w-[540px] leading-[1.6]"
              >
                Now you can inventory an entire house. Print clear price tags. Track sold items. See the value of what is left without replacing the way you already take payment.
              </motion.p>

              <div className="mt-10">
                <div className="flex flex-col gap-3 sm:grid sm:grid-cols-[max-content_max-content] sm:items-start sm:gap-x-3 sm:gap-y-0">
                  <div className="flex flex-col items-center sm:items-stretch">
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => goPrimary("hero")}
                      className="inline-flex min-h-[58px] items-center justify-center gap-2 px-8 rounded-2xl bg-white text-blue-950 text-base font-extrabold shadow-[0_12px_30px_rgba(0,0,0,0.22)] hover:shadow-[0_16px_36px_rgba(0,0,0,0.26)] transition whitespace-nowrap w-full sm:w-auto"
                    >
                      {primaryLabel}
                      {isSignedIn ? <ArrowRight /> : null}
                    </motion.button>
                    {!isSignedIn && (
                      <div className="mt-3 text-center text-[13px] font-semibold text-white/74">
                        No credit card required
                      </div>
                    )}
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => goTo("/pricing", "hero")}
                    className="inline-flex min-h-[58px] items-center justify-center gap-2 px-7 rounded-2xl border border-white/18 bg-white/8 text-white text-base font-extrabold hover:bg-white/14 transition whitespace-nowrap w-full sm:w-auto"
                  >
                    See Pricing
                    <ArrowRight />
                  </motion.button>
                </div>
              </div>
            </div>

            <div className="relative lg:pt-2 lg:pl-12 xl:pl-16">
              <div className="max-w-[1320px] ml-auto rounded-[30px] border border-white/14 bg-[rgba(255,255,255,0.18)] backdrop-blur-xl shadow-[0_22px_64px_rgba(0,0,0,0.24)] overflow-hidden">
                <div className="px-5 pt-3.5 pb-3 bg-[linear-gradient(180deg,rgba(17,29,86,0.96)_0%,rgba(17,29,86,0.84)_100%)] border-b border-white/12">
                  <div className="text-white text-[21px] sm:text-[23px] font-extrabold leading-tight drop-shadow-[0_1px_1px_rgba(0,0,0,0.22)]">Know what matters.</div>
                  <div className="mt-1 text-white/90 text-[16px] leading-relaxed max-w-[520px]">
                    See where the value is, what has sold, and what is left.
                  </div>
                </div>
                <div className="border-y border-slate-300 bg-white/95">
                  <img
                    src={DASHBOARD_IMAGE}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = DASHBOARD_FALLBACK;
                    }}
                    alt="Scucci dashboard showing sale totals and room or category breakdowns"
                    className="block w-full object-contain object-center bg-white"
                    style={{ height: "320px" }}
                    loading="lazy"
                    draggable="false"
                  />
                </div>
                <div className="px-4 pt-3 pb-3 grid grid-cols-2 gap-3 bg-[rgba(255,255,255,0.22)] border-t border-slate-300">
                  <div className="rounded-[16px] bg-white px-4 py-3 shadow-[0_4px_12px_rgba(15,23,42,0.08)] border border-slate-300">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Rooms</div>
                    <div className="mt-1 text-blue-950 text-[16px] font-extrabold leading-tight">See value by room</div>
                  </div>
                  <div className="rounded-[16px] bg-white px-4 py-3 shadow-[0_4px_12px_rgba(15,23,42,0.08)] border border-slate-300">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Categories</div>
                    <div className="mt-1 text-blue-950 text-[16px] font-extrabold leading-tight">See value by category</div>
                  </div>
                  <div className="rounded-[16px] bg-white px-4 py-3 shadow-[0_4px_12px_rgba(15,23,42,0.08)] border border-slate-300">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Sales</div>
                    <div className="mt-1 text-blue-950 text-[16px] font-extrabold leading-tight">Track sold items</div>
                  </div>
                  <div className="rounded-[16px] bg-white px-4 py-3 shadow-[0_4px_12px_rgba(15,23,42,0.08)] border border-slate-300">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Remaining</div>
                    <div className="mt-1 text-blue-950 text-[16px] font-extrabold leading-tight">See what is left</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REASSURANCE STRIP */}
      <section className="w-full bg-white border-b border-gray-200 -mt-px">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4">
          <div className="grid md:grid-cols-3 gap-3 md:gap-4 text-center">
            {[
              "Start with one room.",
              "Price from anywhere.",
              "Print price tags in bulk.",
            ].map((item) => (
              <div
                key={item}
                className="rounded-[18px] border border-blue-100 bg-white px-5 py-4 text-blue-950 font-extrabold text-[15px] shadow-sm"
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section id="outcomes" className="w-full bg-slate-50 border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-blue-950">What Scucci helps you stay on top of</h2>
            <p className="mt-4 text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto">
              Scucci helps you stay organized before the sale starts, while it is running, and when it is time to decide what to discount.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="rounded-[30px] border border-blue-100 bg-white p-9 shadow-sm">
              <div className="text-2xl font-extrabold text-blue-950">See where the value is</div>
              <p className="mt-3 text-gray-700 text-lg leading-relaxed">
                See which rooms and categories deserve your attention first.
              </p>
            </div>

            <div className="rounded-[30px] border border-blue-100 bg-white p-9 shadow-sm">
              <div className="text-2xl font-extrabold text-blue-950">Keep checkout moving</div>
              <p className="mt-3 text-gray-700 text-lg leading-relaxed">
                Print clear price tags with titles to educate buyers and reduce confusion and negotiating at checkout.
              </p>
            </div>

            <div className="rounded-[30px] border border-blue-100 bg-white p-9 shadow-sm">
              <div className="text-2xl font-extrabold text-blue-950">Track what has sold</div>
              <p className="mt-3 text-gray-700 text-lg leading-relaxed">
                Scan QR codes or search by item title to record sales and keep an accurate picture of what is still active.
              </p>
            </div>

            <div className="rounded-[30px] border border-blue-100 bg-white p-9 shadow-sm">
              <div className="text-2xl font-extrabold text-blue-950">Make better markdown decisions</div>
              <p className="mt-3 text-gray-700 text-lg leading-relaxed">
                Know the value of each room and item category before you start cutting prices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STATS PROOF */}
      <section className="w-full bg-white border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-blue-950">See what matters during the sale</h2>
            <p className="mt-4 text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto">
              Scucci helps you see where the value is by room and category, and what needs attention before you start marking things down.
            </p>
          </div>

          <div className="mt-12 grid lg:grid-cols-2 gap-6 lg:gap-8 items-start">
            <div className="rounded-[30px] border border-blue-100 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.08)] overflow-hidden">
              <div className="px-6 py-5 border-b border-blue-100 bg-blue-50/60">
                <div className="text-2xl font-extrabold text-blue-950">See value by room</div>
                <p className="mt-2 text-gray-700 text-lg leading-relaxed">
                  Room-by-room totals help you see where the value is concentrated before sale day and as the weekend moves.
                </p>
              </div>
              <div className="bg-slate-100 px-4 pb-4 pt-3">
                <div className="rounded-[22px] border border-slate-200 bg-white shadow-[0_10px_26px_rgba(15,23,42,0.08)] overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 bg-slate-50">
                    <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
                    <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
                    <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
                  </div>
                  <img
                    src={STATS_SECTION_IMAGE}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = STATS_SECTION_FALLBACK;
                    }}
                    alt="Scucci section breakdown showing remaining tagged value by room"
                    className="block w-full h-auto object-contain bg-white"
                    loading="lazy"
                    draggable="false"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-[30px] border border-blue-100 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.08)] overflow-hidden">
              <div className="px-6 py-5 border-b border-blue-100 bg-blue-50/60">
                <div className="text-2xl font-extrabold text-blue-950">See what is still sitting</div>
                <p className="mt-2 text-gray-700 text-lg leading-relaxed">
                  Spot the highest-priced unsold items by category so you know what deserves attention before you start discounting the whole sale.
                </p>
              </div>
              <div className="bg-slate-100 px-4 pb-4 pt-3">
                <div className="rounded-[22px] border border-slate-200 bg-white shadow-[0_10px_26px_rgba(15,23,42,0.08)] overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 bg-slate-50">
                    <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
                    <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
                    <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
                  </div>
                  <img
                    src={STATS_UNSOLD_IMAGE}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = STATS_UNSOLD_FALLBACK;
                    }}
                    alt="Scucci top unsold items by category showing highest priced unsold inventory"
                    className="block w-full h-auto object-contain bg-white"
                    loading="lazy"
                    draggable="false"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* START SMALL */}
      <section id="start-small" className="w-full bg-white border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 md:py-20">
          <div className="grid lg:grid-cols-[1fr_1fr] gap-10 lg:gap-14 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-blue-950">Start with one room, not the whole house.</h2>
              <p className="mt-4 text-lg text-gray-700 leading-relaxed max-w-2xl">
                You do not need to build the whole sale at once. Start with a bedroom, the garage, the basement, or the living room. Review the draft inventory, make changes if needed, and decide how far you want to go from there.
              </p>

              <div className="mt-8 grid gap-4">
                {[
                  {
                    title: "Upload one room",
                    desc: "Use the photos you already take. Scucci can identify multiple items in each image.",
                  },
                  {
                    title: "Review the draft",
                    desc: "Edit titles, prices, and quantities as much or as little as you like.",
                  },
                  {
                    title: "Print tags or keep going",
                    desc: "Work at your own pace, from home or on-site.",
                  },
                ].map((item, index) => (
                  <div key={item.title} className="rounded-2xl border border-blue-100 bg-white px-5 py-5 shadow-sm flex gap-4">
                    <div className="flex-shrink-0 h-10 w-10 rounded-2xl bg-blue-950 text-white flex items-center justify-center font-extrabold">
                      {index + 1}
                    </div>
                    <div>
                      <div className="text-lg font-extrabold text-blue-950">{item.title}</div>
                      <div className="mt-1 text-gray-700">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => goPrimary("start_small")}
                  className="inline-flex h-13 min-h-[52px] items-center justify-center gap-2 px-7 rounded-2xl bg-blue-950 text-white font-extrabold shadow hover:bg-blue-900 transition w-full sm:w-auto"
                >
                  {primaryLabel}
                  {isSignedIn ? <ArrowRight /> : null}
                </motion.button>

                <div className="text-sm font-semibold text-slate-600">
                  Try it on one room first and see how it fits your workflow.
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="rounded-[30px] border border-blue-100 bg-white shadow-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-blue-100 bg-blue-50/60">
                  <div className="text-blue-950 text-base font-extrabold">Start Small</div>
                  <div className="text-slate-600 text-sm">Use one room as your first test.</div>
                </div>
                <img
                  src={START_SMALL_IMAGE}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = START_SMALL_FALLBACK;
                  }}
                  alt="Scucci screen showing uploaded photos and draft inventory results"
                  className="block w-full h-auto object-contain"
                  loading="lazy"
                  draggable="false"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PHOTO EFFICIENCY */}
      <section id="how" className="w-full bg-slate-50 border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-4 pb-10 md:pt-6 md:pb-12">
          <div className="rounded-[30px] border border-blue-100 bg-[linear-gradient(180deg,rgba(239,246,255,0.88)_0%,rgba(219,234,254,0.62)_100%)] p-5 md:p-6 lg:p-8 overflow-hidden shadow-sm">
            <div className="grid lg:grid-cols-[0.82fr_1.18fr] gap-8 lg:gap-12 items-start">
              <div className="order-1 self-start">
                <div className="w-full lg:max-w-[520px]">
                  <div className="inline-flex items-center rounded-full bg-white text-blue-950 text-[11px] font-bold uppercase tracking-[0.12em] px-4 py-2 border border-blue-100 shadow-sm mb-4">
                    Original photo
                  </div>
                  <div className="rounded-[22px] overflow-hidden">
                    <img
                      src={PHOTO_SOURCE_IMAGE}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = PHOTO_SOURCE_FALLBACK;
                      }}
                      alt="Example photo used to create multiple inventory items"
                      className="block h-auto w-full object-contain object-top"
                      loading="lazy"
                      draggable="false"
                    />
                  </div>
                </div>
              </div>

              <div className="order-2 max-w-3xl self-start pt-1 lg:pt-10">
                <h2 className="text-3xl sm:text-4xl lg:text-[42px] font-extrabold tracking-[-0.02em] leading-[1.02] text-blue-950">
                  One photo, multiple inventory items.
                </h2>
                <p className="mt-8 text-lg sm:text-xl text-gray-700 leading-[1.75] max-w-[720px]">
                  Most people assume one photo means one item. Not with Scucci. The lines below came straight from this single image and are already strong enough to use on price tags.
                </p>
                <p className="mt-6 text-base sm:text-lg text-gray-700 leading-[1.8] max-w-[720px]">
                  That means less typing by hand, less setup before the sale, and a faster way to build inventory from the photos you already take. Start with one room. A few staged photos can turn into dozens of inventory items, then into price tags, exports, and sale tracking.
                </p>
                <p className="mt-6 text-base sm:text-lg text-gray-700 leading-[1.8] max-w-[720px]">
                  You do not need to photograph every item one by one. Scucci was built for real estate-sale workflow, where normal room photos can do much more work for you.
                </p>
                <div className="mt-8 inline-flex items-center whitespace-nowrap rounded-full bg-white text-blue-950 text-sm font-extrabold px-4 py-2 border border-blue-100 shadow-sm">
                  The image to the left produced the unedited results below
                </div>
              </div>
            </div>

            <div className="mt-6 w-full lg:max-w-[1040px] lg:ml-auto lg:-mt-[270px] relative z-10">
              <div className="relative">
                <div className="rounded-[22px] overflow-hidden">
                  <img
                    src={PHOTO_EFFICIENCY_IMAGE}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = PHOTO_EFFICIENCY_FALLBACK;
                    }}
                    alt="Scucci inventory dashboard showing multiple inventory lines created from uploaded photos"
                    className="block h-auto w-full object-contain object-top -mb-[2.5%]"
                    loading="lazy"
                    draggable="false"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMMON CONCERNS */}
      <section id="concerns" className="w-full bg-white border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 md:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-blue-950">Four questions most people ask first</h2>
            <p className="mt-4 text-lg text-gray-700">
              Quick answers to the questions that matter before you start a trial.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div className="rounded-[28px] border border-blue-100 bg-white p-8 shadow-sm">
              <div className="text-xl font-extrabold text-blue-950">What is the best way to get started?</div>
              <div className="mt-3 text-gray-700 text-lg leading-relaxed">Start with one room. Upload the photos you already take from your phone, or upload images from your computer. If you have internet access, you can get started in minutes.</div>
            </div>

            <div className="rounded-[28px] border border-blue-100 bg-white p-8 shadow-sm">
              <div className="text-xl font-extrabold text-blue-950">Will this save me time?</div>
              <div className="mt-3 text-gray-700 text-lg leading-relaxed">Yes. Scucci turns normal sale photos into usable inventory lines, which means less typing by hand and fewer handwritten descriptions customers struggle to read.</div>
            </div>

            <div className="rounded-[28px] border border-blue-100 bg-white p-8 shadow-sm">
              <div className="text-xl font-extrabold text-blue-950">Do I need any special equipment?</div>
              <div className="mt-3 text-gray-700 text-lg leading-relaxed">No. You can work from your phone or computer anywhere you have internet access. Use your current label maker or the printer you already have.</div>
            </div>

            <div className="rounded-[28px] border border-blue-100 bg-white p-8 shadow-sm">
              <div className="text-xl font-extrabold text-blue-950">Does Scucci decide the prices for me?</div>
              <div className="mt-3 text-gray-700 text-lg leading-relaxed">No. Scucci creates a draft for you to review. You can change prices, titles, and quantities before you print tags or move forward. You can also mark down your sale throughout the day using Today’s Discount.</div>
            </div>
          </div>
        </div>
      </section>

      {/* MID PAGE CTA */}
      <section className="w-full bg-slate-50 border-b border-gray-100">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-blue-950">Start with one room and see how quickly it comes together.</h2>
          <p className="mt-4 text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Use the photos you already take, review the draft, make changes if needed, and decide how far you want to go from there.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => goPrimary("mid_cta")}
              className="inline-flex h-13 min-h-[52px] items-center justify-center gap-2 px-8 rounded-2xl bg-blue-950 text-white font-extrabold shadow hover:bg-blue-900 transition w-full sm:w-auto"
            >
              {primaryLabel}
              {isSignedIn ? <ArrowRight /> : null}
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => goTo("/pricing", "mid_cta")}
              className="inline-flex h-13 min-h-[52px] items-center justify-center gap-2 px-8 rounded-2xl border border-blue-950 text-blue-950 font-extrabold hover:bg-blue-50 transition w-full sm:w-auto"
            >
              See Pricing
              <ArrowRight />
            </motion.button>
          </div>

          <div className="mt-4 text-base text-slate-600">
            Questions? <a href={CONTACT_MAILTO} className="font-extrabold text-blue-950 underline underline-offset-4">Email us.</a>
          </div>
        </div>
      </section>

      {/* PROFESSIONALISM */}
      <section className="w-full bg-blue-950 text-white border-b border-blue-900/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 md:py-20">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-10 lg:gap-14 items-center">
            <div>
              <div className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-wide text-white backdrop-blur mb-4 border border-white/10">
                Built for real sale days
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold">Run a sale that feels more organized and professional.</h2>
              <p className="mt-4 text-lg text-white/85 leading-relaxed max-w-2xl">
                Scucci helps you run a cleaner sale, present items more clearly, and save hours of manual work before and during sale day.
              </p>

              <div className="mt-8 grid gap-4">
                <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-5 backdrop-blur">
                  <div className="text-xl font-extrabold">Clear price tags reduce confusion</div>
                  <div className="mt-2 text-white/80">Educate shoppers on brand or era, reduce tag switching and theft, and help staff look up inventory if a tag gets removed.</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-5 backdrop-blur">
                  <div className="text-xl font-extrabold">Sales are easier to track</div>
                  <div className="mt-2 text-white/80">Record sold items as the day moves so you can see the value of what is left and make better markdown decisions.</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-5 backdrop-blur">
                  <div className="text-xl font-extrabold">Exports free up time</div>
                  <div className="mt-2 text-white/80">Save hours of typing, and let Scucci create inventory and sales reports for you.</div>
                </div>
              </div>
            </div>

            <div>
              <div className="rounded-[30px] overflow-hidden border border-white/10 shadow-2xl bg-white/5">
                <img
                  src={OPS_IMAGE}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = OPS_FALLBACK;
                  }}
                  alt="Scucci tags and estate sale workflow in a real-world setting"
                  className="block w-full h-full object-cover min-h-[320px] lg:min-h-[520px]"
                  loading="lazy"
                  draggable="false"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="w-full bg-white border-b border-gray-100">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-16 md:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-blue-950">More questions</h2>
            <p className="mt-4 text-lg text-gray-700">
              A few more practical answers before you get started.
            </p>
          </div>

          <div className="mt-10 space-y-4">
            <QA
              q="What if the suggested price or description is wrong?"
              a="You stay in control. Scucci creates a draft, and you can change or delete any item before you print tags or move forward."
            />
            <QA
              q="Can I track my sales?"
              a="Yes. You can track your sales, discount the sale, track sales tax and credit card fees, and create manual items at checkout."
            />
            <QA
              q="What kind of photos work best?"
              a="Use good lighting and aim for about 3 to 6 items per photo. Scucci can often pull more from a single photo, but 3 to 6 is the sweet spot for speed, cleaner identification, and fewer corrections."
            />
            <QA
              q="Can I start pricing before the sale is set up?"
              a="Yes. As soon as you have photos or access to the house, you can start building draft inventory and pricing earlier."
            />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="w-full bg-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-16 md:py-20 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-blue-950">Start with the sale photos you already have.</h2>
          <p className="mt-4 text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
            See what matters, print clear tags, and build a cleaner, more organized sale from the photos you already have.
          </p>

          <div className="mt-8 flex flex-col items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => goPrimary("final_cta")}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-blue-950 text-white text-sm md:text-base font-extrabold shadow hover:bg-blue-900 transition w-full sm:w-auto"
            >
              {primaryLabel}
              {isSignedIn ? <ArrowRight /> : null}
            </motion.button>
            {!isSignedIn && (
              <div className="text-sm text-slate-600">No credit card required.</div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}